import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { fetchJSON } from "common/utils/fetchJSON";
import Icon from "components/Icon/Icon";
import Button from "common/components/Button/Button";
import type { FAQ } from "types";
import useList from "hooks/useList";

type SortableRowProps = {
  faq: FAQ;
};

function SortableRow({ faq }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: faq.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg mb-2 shadow-sm"
    >
      <button
        type="button"
        className="cursor-grab text-gray-400 hover:text-gray-600 focus:outline-none"
        {...attributes}
        {...listeners}
      >
        <Bars3Icon className="h-5 w-5" />
      </button>
      <p className="flex-1 text-sm font-medium text-gray-900 truncate">
        {faq.question}
      </p>
    </div>
  );
}

type Props = {
  onClose: () => void;
};

function FAQReorder({ onClose }: Props) {
  const { t } = useTranslation();
  const { items, isFetching } = useList<FAQ>("faqs", {
    defaultSort: "order:ASC",
    defaultItemsPerPage: 200,
  });
  const [orderedItems, setOrderedItems] = useState<FAQ[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    if (items) {
      setOrderedItems(items);
    }
  }, [items]);

  function handleDragEnd(event: any) {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setOrderedItems((prev) => {
        const oldIndex = prev.findIndex((item) => item.id === active.id);
        const newIndex = prev.findIndex((item) => item.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  }

  async function handleSave() {
    setIsSaving(true);
    try {
      await Promise.all(
        orderedItems.map((faq, index) =>
          fetchJSON({
            url: `faqs/${faq.id}`,
            method: "PUT",
            payload: { data: { order: index + 1 } },
          }),
        ),
      );
      onClose();
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="px-4">
      {/* Header — style CrudHeader */}
      <div className="flex flex-col lg:flex-row justify-between lg:items-center w-full pt-4 mb-6 gap-4">
        <div className="flex flex-col items-center gap-4 font-bold text-primary text-2xl">
          <div>
            <h1 className="text-black text-3xl">{t("faqs.listTitle")}</h1>
            <p className="text-m font-light mt-2 text-primary-500">
              {t("faqs.reorderHint")}
            </p>
          </div>
        </div>

        <div className="flex flex-row gap-3 items-center">
          <Button
            title={t("common.cancel")}
            onClick={onClose}
            type="error"
            icon="XMarkIcon"
            className="py-3 px-4"
          />
          {/* eslint-disable-next-line react/jsx-no-bind */}
          <Button
            title={t("common.save")}
            onClick={handleSave}
            disabled={isSaving || isFetching}
            loading={isSaving}
            icon="CheckIcon"
            className="bg-gradient-to-r from-secondary-300 to-secondary text-white hover:opacity-90 py-3 px-4"
          />
        </div>
      </div>

      {/* List */}
      {isFetching ? (
        <p className="text-center text-gray-400 py-8">{t("common.loading")}</p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          // eslint-disable-next-line react/jsx-no-bind
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={orderedItems.map((faq) => faq.id)}
            strategy={verticalListSortingStrategy}
          >
            {orderedItems.map((faq) => (
              <SortableRow key={faq.id} faq={faq} />
            ))}
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

export default FAQReorder;
